/**
 * Utility function to standardize API requests by ensuring that an Authorization token is always included in the headers.
 * This function simplifies the process of making authenticated requests from both client and server environments
 * by requiring the caller to provide the token.
 *
 * @param {string} url - The URL to make the request to
 * @param {RequestInit} [options={}] - The fetch options to use for the request
 * @param {string} token - The authentication token to include in the Authorization header
 * @param {Function} [ensureTokenIsValid] - Optional async callback to validate/refresh token before the request, returns access token or null
 * @returns {Promise<Response>} - The fetch response promise
 * @throws {Error} - If ensureTokenIsValid fails or fetch fails
 * 
 * @note: If using for token refresh please be mindful, its not causing parallel token refreshes in the service
 * For example: if there are multiple api requests happening at the same time please refresh the token outside of this function
 */
export async function emarketerFetchWithAuthAsync(
  url: string,
  options: RequestInit = {},
  token: string,
  ensureNonExpiredAccessTokenAsync?: () => Promise<string | null>
): Promise<Response> {
  let finalToken = token;
  // Execute ensureTokenIsValid callback with error handling
  if (ensureNonExpiredAccessTokenAsync) {
    try {
      const validatedToken = await ensureNonExpiredAccessTokenAsync();

      if (validatedToken) {
        finalToken = validatedToken;
      }
      
    } catch (error) {
      throw new Error(
        `ensureNonExpiredAccessTokenAsync callback failed: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    }
  }

  // Set up headers with authorization
  const headers = new Headers(options.headers);
  headers.set('Authorization', `Bearer ${finalToken}`);

  // Make the fetch request
  let response: Response;
  try {
    response = await fetch(url, { ...options, headers });
  } catch (error) {
    throw new Error(
      `Fetch request failed: ${
        error instanceof Error ? error.message : String(error)
      }`
    );
  }

  return response;
}
